//Built-in libraries

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
//Third party libraries
import { useParams } from 'react-router';
import ProjectSubsidiesHookFieldArray, {
  projectSubsidiesValidationSchema,
} from '@/components/Sherpa/Projects/ProjectSubsidiesHookFieldArray';
//Custom components
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { patchProject } from '../../../../../../../api/Sherpa/Project';
import type { IPatchProjectServicePayload } from '../../../../../../../api/Sherpa/Project/modules/ProjectCore/types';
//Assets andd Miscellaneous
import { SherpaProjectReactQueryKey } from '../../../../../../../constants/ReactQueryKeys';
import { useProject } from '../../../../../../../hooks/ReactQueryHooks/useProjectQuery';
import { notifyUser } from '../../../../../../../store/Toast';
import type { ReactSelectOption } from '../../../../../../../types/Constants';
import type { ProjectSubsidiesInterface } from '../../../../../../../types/Project';
//Services
import { sanitizeResponseError } from '../../../../../../../utils';

interface IProjectSubsidiesLineItem {
  name: ReactSelectOption;
  amount: number;
}

interface IUpdateProjectSubsidiesProps {
  handleModal: () => void;
}

export default function UpdateProjectSubsidies({ handleModal }: IUpdateProjectSubsidiesProps) {
  const { projectId } = useParams<string>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const fetchProject = useProject({ projectId: parseInt(projectId!, 10) });
  const subsidies = fetchProject?.data?.data?.subsidies;

  const updateProjectSubsidies = useMutation(
    (data: Partial<IPatchProjectServicePayload>) => patchProject({ projectId: Number(projectId), data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT]);
        handleModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project subsidies update successful',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const onSubmit = (formData: { subsidies: IProjectSubsidiesLineItem[] }) => {
    const data = formData.subsidies.map((s) => ({
      name: s.name.value,
      amount: s.amount,
    }));
    updateProjectSubsidies.mutate({ subsidies: data });
  };

  //Transforming for react select component default values
  const transformedSubsidiesDefaultValues = subsidies?.map((s: ProjectSubsidiesInterface) => ({
    name: { label: s.name, value: s.name },
    amount: s.amount,
  }));

  return (
    <Modal>
      <HookFormProvider
        defaultValues={{
          subsidies: transformedSubsidiesDefaultValues,
        }}
        resolver={yupResolver(projectSubsidiesValidationSchema) as any}
        mode='all'
      >
        <HookForm onSubmit={onSubmit}>
          <h2 className='mt-0'>Project Subsidies</h2>
          <ProjectSubsidiesHookFieldArray />
          <div className='flex justify-end gap-4 mt-5'>
            <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={handleModal} />
            <Button label={`${updateProjectSubsidies.isLoading ? 'Saving...' : 'Save'}`} btnStyle='btn-primary' type='submit' />
          </div>
        </HookForm>
      </HookFormProvider>
    </Modal>
  );
}
